import React, { useState, useCallback } from 'react';
import { Copy, Download, RefreshCw, Code, Settings, Zap } from 'lucide-react';

interface PhpFormatterProps {}

const PhpFormatter: React.FC<PhpFormatterProps> = () => {
  const [inputCode, setInputCode] = useState<string>('');
  const [formattedCode, setFormattedCode] = useState<string>('');
  const [indentSize, setIndentSize] = useState<number>(4);
  const [maxLineLength, setMaxLineLength] = useState<number>(120);
  const [bracketStyle, setBracketStyle] = useState<'same-line' | 'new-line'>('same-line');
  const [isFormatting, setIsFormatting] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  const phpKeywords = [
    'abstract', 'and', 'array', 'as', 'break', 'callable', 'case', 'catch', 'class',
    'clone', 'const', 'continue', 'declare', 'default', 'die', 'do', 'echo', 'else',
    'elseif', 'empty', 'enddeclare', 'endfor', 'endforeach', 'endif', 'endswitch',
    'endwhile', 'eval', 'exit', 'extends', 'final', 'finally', 'for', 'foreach',
    'function', 'global', 'goto', 'if', 'implements', 'include', 'include_once',
    'instanceof', 'insteadof', 'interface', 'isset', 'list', 'namespace', 'new',
    'or', 'print', 'private', 'protected', 'public', 'require', 'require_once',
    'return', 'static', 'switch', 'throw', 'trait', 'try', 'unset', 'use', 'var',
    'while', 'xor', 'yield', 'yield from'
  ];

  const phpBuiltins = [
    'array_keys', 'array_values', 'array_merge', 'array_push', 'array_pop', 'count',
    'strlen', 'substr', 'strpos', 'str_replace', 'explode', 'implode', 'trim',
    'ltrim', 'rtrim', 'strtolower', 'strtoupper', 'ucfirst', 'ucwords', 'md5',
    'sha1', 'hash', 'json_encode', 'json_decode', 'serialize', 'unserialize',
    'file_get_contents', 'file_put_contents', 'fopen', 'fclose', 'fread', 'fwrite',
    'mysql_connect', 'mysqli_connect', 'PDO', 'preg_match', 'preg_replace',
    'filter_var', 'htmlspecialchars', 'htmlentities', 'strip_tags', 'addslashes',
    'stripslashes', 'date', 'time', 'mktime', 'strtotime', 'number_format', 'round'
  ];

  const phpSuperglobals = ['$_GET', '$_POST', '$_REQUEST', '$_SESSION', '$_COOKIE', '$_SERVER', '$_ENV', '$_FILES', '$GLOBALS'];

  const formatPhp = useCallback((code: string): string => {
    if (!code.trim()) return '';

    let formatted = code;
    const indent = ' '.repeat(indentSize);
    
    // Split into lines
    const lines = formatted.split('\n');
    const formattedLines: string[] = [];
    let currentIndentLevel = 0;
    let inPhpTag = false;
    let inMultilineComment = false;
    let inString = false;
    let stringDelimiter = '';

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      const trimmedLine = line.trim();
      
      // Skip empty lines but preserve them
      if (!trimmedLine) {
        formattedLines.push('');
        continue;
      }

      // Check for PHP opening/closing tags
      if (trimmedLine.includes('<?php') || trimmedLine.includes('<?=')) {
        inPhpTag = true;
        formattedLines.push(trimmedLine);
        continue;
      }
      
      if (trimmedLine.includes('?>')) {
        inPhpTag = false;
        formattedLines.push(trimmedLine);
        continue;
      }

      // Handle HTML outside PHP tags
      if (!inPhpTag) {
        formattedLines.push(line);
        continue;
      }

      // Handle multiline comments
      if (!inString) {
        if (trimmedLine.includes('/*') && !trimmedLine.includes('*/')) {
          inMultilineComment = true;
        } else if (trimmedLine.includes('*/') && inMultilineComment) {
          inMultilineComment = false;
        }
      }

      // Handle strings
      if (!inMultilineComment) {
        const stringMatches = trimmedLine.match(/["']/g);
        if (stringMatches) {
          // Simple string detection (not perfect but works for basic cases)
          const quoteCount = stringMatches.length;
          if (quoteCount % 2 === 1) {
            inString = !inString;
            if (inString) {
              stringDelimiter = stringMatches[stringMatches.length - 1];
            }
          }
        }
      }

      // Don't format inside multiline comments or strings
      if (inMultilineComment || inString) {
        formattedLines.push(line);
        continue;
      }

      // Handle dedent for certain keywords
      if (trimmedLine.match(/^(else|elseif|catch|finally|case|default)[\s{:]/)) {
        currentIndentLevel = Math.max(0, currentIndentLevel - 1);
      }

      // Handle closing braces
      if (trimmedLine.startsWith('}')) {
        currentIndentLevel = Math.max(0, currentIndentLevel - 1);
      }

      // Apply indentation
      let indentedLine = indent.repeat(currentIndentLevel) + trimmedLine;
      
      // Handle bracket styles
      if (bracketStyle === 'new-line') {
        // Move opening braces to new line
        if (trimmedLine.includes('{') && !trimmedLine.startsWith('{')) {
          const parts = trimmedLine.split('{');
          if (parts.length === 2 && parts[1].trim() === '') {
            indentedLine = indent.repeat(currentIndentLevel) + parts[0].trim();
            formattedLines.push(indentedLine);
            formattedLines.push(indent.repeat(currentIndentLevel) + '{');
          } else {
            formattedLines.push(indentedLine);
          }
        } else {
          formattedLines.push(indentedLine);
        }
      } else {
        formattedLines.push(indentedLine);
      }

      // Handle indent for next line
      if (trimmedLine.includes('{')) {
        currentIndentLevel++;
      }
      
      // Handle specific PHP structures
      if (trimmedLine.match(/^(if|for|foreach|while|switch|function|class|interface|trait)\s*\([^)]*\)\s*$/)) {
        currentIndentLevel++;
      }

      // Handle array formatting
      if (trimmedLine.includes('array(') || trimmedLine.includes('[')) {
        const openBrackets = (trimmedLine.match(/[\[\(]/g) || []).length;
        const closeBrackets = (trimmedLine.match(/[\]\)]/g) || []).length;
        if (openBrackets > closeBrackets) {
          currentIndentLevel += (openBrackets - closeBrackets);
        }
      }
    }

    return formattedLines
      .map(line => line.trimEnd()) // Remove trailing spaces
      .join('\n')
      .replace(/\n{3,}/g, '\n\n'); // Limit consecutive empty lines to 2
  }, [indentSize, bracketStyle]);

  const handleFormat = useCallback(async () => {
    if (!inputCode.trim()) return;
    
    setIsFormatting(true);
    
    // Simulate formatting delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const formatted = formatPhp(inputCode);
    setFormattedCode(formatted);
    setIsFormatting(false);
  }, [inputCode, formatPhp]);

  const handleCopy = useCallback(async () => {
    if (!formattedCode) return;
    
    try {
      await navigator.clipboard.writeText(formattedCode);
      const button = document.getElementById('copy-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, [formattedCode]);

  const handleDownload = useCallback(() => {
    if (!formattedCode) return;
    
    const blob = new Blob([formattedCode], { type: 'application/x-php' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted_code.php';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [formattedCode]);

  const handleClear = useCallback(() => {
    setInputCode('');
    setFormattedCode('');
  }, []);

  const highlightPhp = useCallback((code: string): string => {
    if (!code) return '';
    
    let highlighted = code;
    
    // Escape HTML
    highlighted = highlighted
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Highlight PHP opening/closing tags
    highlighted = highlighted.replace(/(&lt;\?php|\?&gt;|&lt;\?=)/g, '<span class="text-red-600 font-bold">$1</span>');
    
    // Highlight PHP keywords
    phpKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-purple-600 font-semibold">${keyword}</span>`);
    });
    
    // Highlight built-in functions
    phpBuiltins.forEach(func => {
      const regex = new RegExp(`\\b${func}\\b(?=\\s*\\()`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-blue-600 font-semibold">${func}</span>`);
    });
    
    // Highlight superglobals
    phpSuperglobals.forEach(global => {
      const escapedGlobal = global.replace(/\$/g, '\\$');
      const regex = new RegExp(`\\${escapedGlobal}\\b`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-indigo-600 font-bold">${global}</span>`);
    });
    
    // Highlight variables
    highlighted = highlighted.replace(/\$[a-zA-Z_][a-zA-Z0-9_]*/g, '<span class="text-cyan-600">$&</span>');
    
    // Highlight strings
    highlighted = highlighted.replace(/(\"([^\"\\\\]|\\\\.)*\"|'([^'\\\\]|\\\\.)*')/g, '<span class="text-green-600">$1</span>');
    
    // Highlight numbers
    highlighted = highlighted.replace(/\b\d+(\.\d+)?\b/g, '<span class="text-orange-600">$&</span>');
    
    // Highlight comments
    highlighted = highlighted.replace(/(\/\/.*$|#.*$)/gm, '<span class="text-gray-500 italic">$1</span>');
    highlighted = highlighted.replace(/(\/\*[\s\S]*?\*\/)/g, '<span class="text-gray-500 italic">$1</span>');
    
    // Highlight class names and functions
    highlighted = highlighted.replace(/\b(class|interface|trait)\s+(\w+)/g, 
      '$1 <span class="text-yellow-600 font-bold">$2</span>');
    highlighted = highlighted.replace(/\bfunction\s+(\w+)/g, 
      'function <span class="text-cyan-600 font-bold">$1</span>');
    
    // Highlight constants
    highlighted = highlighted.replace(/\b[A-Z_][A-Z0-9_]*\b/g, '<span class="text-red-500 font-semibold">$&</span>');
    
    return highlighted;
  }, []);

  const exampleCode = `<?php
declare(strict_types=1);

namespace App\\Controllers;

use App\\Models\\User;
use App\\Services\\EmailService;
use Illuminate\\Http\\Request;
use Illuminate\\Http\\JsonResponse;

/**
 * User controller for handling user-related operations
 */
class UserController extends BaseController
{
    private EmailService $emailService;
    private const MAX_USERS_PER_PAGE = 50;

    public function __construct(EmailService $emailService)
    {
        $this->emailService = $emailService;
        parent::__construct();
    }

    /**
     * Get all users with pagination
     */
    public function index(Request $request): JsonResponse
    {
        $page = (int) $request->get('page', 1);
        $limit = min((int) $request->get('limit', 10), self::MAX_USERS_PER_PAGE);
        
        try {
            $users = User::with(['profile', 'roles'])
                ->where('active', true)
                ->orderBy('created_at', 'desc')
                ->paginate($limit, ['*'], 'page', $page);

            return response()->json([
                'success' => true,
                'data' => $users->items(),
                'pagination' => [
                    'current_page' => $users->currentPage(),
                    'total_pages' => $users->lastPage(),
                    'total_items' => $users->total()
                ]
            ]);
        } catch (\\Exception $e) {
            return $this->handleError($e, 'Failed to retrieve users');
        }
    }

    /**
     * Create a new user
     */
    public function store(Request $request): JsonResponse
    {
        $validatedData = $request->validate([
            'name' => 'required|string|max:255',
            'email' => 'required|email|unique:users',
            'password' => 'required|string|min:8|confirmed',
            'role' => 'nullable|string|in:admin,user,moderator'
        ]);

        $userData = [
            'name' => $validatedData['name'],
            'email' => strtolower($validatedData['email']),
            'password' => password_hash($validatedData['password'], PASSWORD_ARGON2ID),
            'role' => $validatedData['role'] ?? 'user',
            'created_at' => date('Y-m-d H:i:s'),
            'email_verified_at' => null
        ];

        try {
            $user = User::create($userData);
            
            // Send welcome email
            $this->emailService->sendWelcomeEmail($user);
            
            return response()->json([
                'success' => true,
                'message' => 'User created successfully',
                'data' => $user->toArray()
            ], 201);
        } catch (\\Exception $e) {
            return $this->handleError($e, 'Failed to create user');
        }
    }

    /**
     * Update user information
     */
    public function update(Request $request, int $id): JsonResponse
    {
        $user = User::findOrFail($id);
        
        $rules = [
            'name' => 'sometimes|string|max:255',
            'email' => 'sometimes|email|unique:users,email,' . $id,
            'role' => 'sometimes|string|in:admin,user,moderator'
        ];

        if ($request->has('password')) {
            $rules['password'] = 'string|min:8|confirmed';
        }

        $validatedData = $request->validate($rules);

        if (isset($validatedData['password'])) {
            $validatedData['password'] = password_hash($validatedData['password'], PASSWORD_ARGON2ID);
        }

        $user->update($validatedData);

        return response()->json([
            'success' => true,
            'message' => 'User updated successfully',
            'data' => $user->fresh()->toArray()
        ]);
    }

    /**
     * Delete a user
     */
    public function destroy(int $id): JsonResponse
    {
        $user = User::findOrFail($id);
        
        if ($user->id === auth()->id()) {
            return response()->json([
                'success' => false,
                'message' => 'Cannot delete your own account'
            ], 400);
        }

        $user->delete();

        return response()->json([
            'success' => true,
            'message' => 'User deleted successfully'
        ]);
    }

    /**
     * Handle errors and return appropriate response
     */
    private function handleError(\\Exception $e, string $defaultMessage): JsonResponse
    {
        \\Log::error($e->getMessage(), ['trace' => $e->getTraceAsString()]);
        
        return response()->json([
            'success' => false,
            'message' => app()->environment('production') ? $defaultMessage : $e->getMessage()
        ], 500);
    }
}

// Example of functional PHP code
function calculateTax(float $amount, float $rate = 0.1): float {
    return round($amount * $rate, 2);
}

$products = [
    ['name' => 'Laptop', 'price' => 999.99],
    ['name' => 'Mouse', 'price' => 29.99],
    ['name' => 'Keyboard', 'price' => 79.99]
];

foreach ($products as &$product) {
    $product['tax'] = calculateTax($product['price']);
    $product['total'] = $product['price'] + $product['tax'];
}

echo json_encode($products, JSON_PRETTY_PRINT);
?>`;

  const countLines = (text: string): number => {
    return text ? text.split('\n').length : 0;
  };

  const countWords = (text: string): number => {
    return text ? text.trim().split(/\s+/).filter(word => word.length > 0).length : 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Zap className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">PHP Code Formatter</h1>
                  <p className="text-purple-100">Format and beautify your PHP code</p>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="indent-size" className="text-sm font-medium text-gray-700">
                    Indent Size:
                  </label>
                  <select
                    id="indent-size"
                    value={indentSize}
                    onChange={(e) => setIndentSize(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={2}>2 spaces</option>
                    <option value={4}>4 spaces (PSR-12)</option>
                    <option value={8}>8 spaces</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <label htmlFor="line-length" className="text-sm font-medium text-gray-700">
                    Max Line Length:
                  </label>
                  <select
                    id="line-length"
                    value={maxLineLength}
                    onChange={(e) => setMaxLineLength(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={80}>80 characters</option>
                    <option value={120}>120 characters (PSR-12)</option>
                    <option value={150}>150 characters</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label htmlFor="bracket-style" className="text-sm font-medium text-gray-700">
                    Bracket Style:
                  </label>
                  <select
                    id="bracket-style"
                    value={bracketStyle}
                    onChange={(e) => setBracketStyle(e.target.value as 'same-line' | 'new-line')}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="same-line">Same Line</option>
                    <option value="new-line">New Line (PSR-12)</option>
                  </select>
                </div>
                
                <button
                  onClick={() => setInputCode(exampleCode)}
                  className="px-4 py-2 text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 border border-purple-300 rounded-md transition-colors duration-200"
                >
                  Load Example
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 h-96 lg:h-[600px]">
            {/* Input Panel */}
            <div className="flex flex-col border-r border-gray-200">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Input PHP Code</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleClear}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors duration-200"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleFormat}
                    disabled={isFormatting || !inputCode.trim()}
                    className="flex items-center space-x-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white text-sm rounded transition-colors duration-200"
                  >
                    {isFormatting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4" />
                    )}
                    <span>{isFormatting ? 'Formatting...' : 'Format'}</span>
                  </button>
                </div>
              </div>
              <textarea
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="Paste your PHP code here..."
                className="flex-1 p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
                spellCheck={false}
              />
            </div>

            {/* Output Panel */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Formatted PHP Code</h2>
                <div className="flex space-x-2">
                  <button
                    id="copy-button"
                    onClick={handleCopy}
                    disabled={!formattedCode}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={!formattedCode}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4 bg-gray-900 overflow-auto">
                {formattedCode ? (
                  <pre
                    className="font-mono text-sm text-gray-100 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: highlightPhp(formattedCode)
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Formatted PHP code will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <span>Characters: {inputCode.length}</span>
                <span>Lines: {countLines(inputCode)}</span>
                <span>Words: {countWords(inputCode)}</span>
              </div>
              <div className="text-xs">
                Follows PSR-12 coding standards • Supports PHP 7.4+ syntax • Laravel compatible
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhpFormatter;